import { ACTION_TYPE } from '../core/redux/actionType';
import { IAppState } from '../core/redux/IAppState';
import { Action } from 'redux';
import { ResortMappingObject, RTPMappingProduct, SiriuswareMappingProduct } from './productMappings';
import { assoc } from '../language/module';
import { getProductHydrationErrors } from './hydrationErrors';


/** Rows as loaded, tagged with what they were loaded for so a stale snapshot cannot be replayed. */
export type ResortMappingSnapshot = {
  resortAbbreviation: string;
  posProvider: string;
  rows: RTPMappingProduct[] | SiriuswareMappingProduct[];
};


type RevertResortMappingRowsAction = {
  type: ACTION_TYPE.REVERT_RESORT_MAPPING_ROWS;
  payload: ResortMappingSnapshot;
};

/**
 * Makes an action that can be dispatched to restore product mapping rows to a snapshot taken before editing,
 * discarding unsaved edits that updateRowAction wrote into local state
 * @param payload
 */
export const revertResortMappingRowsAction = (
  payload: ResortMappingSnapshot,
): RevertResortMappingRowsAction => ({
  type: ACTION_TYPE.REVERT_RESORT_MAPPING_ROWS,
  payload: payload
});

export const revertResortMappingRowsReducer = (state: IAppState, action: Action): IAppState => {

  if (action.type !== ACTION_TYPE.REVERT_RESORT_MAPPING_ROWS) return state;

  const snapshot = (action as RevertResortMappingRowsAction).payload;
  const snapshotRows = snapshot?.rows as RTPMappingProduct[];

  if (!snapshotRows?.length) return state;

  // Ids are only unique within a resort and provider
  if (snapshot.resortAbbreviation !== state.selectedResort?.resortAbbreviation) return state;
  if (snapshot.posProvider !== state.resortMappingHistories.posProvider) return state;

  const snapshotById = new Map(snapshotRows.map((row) => [row.id, row]));

  // Match on id rather than replacing the array so the current sort order and any rows added since the
  // snapshot survive the revert
  const revertedMappings = (state.resortMappingHistories.productMappings as RTPMappingProduct[])
    .map((row) => {
      const loaded = snapshotById.get(row.id);
      // Mapping Status and hydration errors can land after the snapshot was taken, so both come from what is loaded
      // now. Mid-edit, a row only takes a status checked against its loaded values.
      return loaded
        ? {
          ...loaded,
          isError:          row.isError,
          errorDescription: row.errorDescription,
          hydrationErrors: getProductHydrationErrors(
            state.productHydrationErrors, loaded.entitlementTypeId, loaded.fulfillmentSeasonId, loaded.ageBin,
          ),
        }
        : row;
    });

  const newObj: ResortMappingObject = {
    posProvider: state.resortMappingHistories.posProvider,
    productMappings: revertedMappings,
    hasError: state.resortMappingHistories.hasError
  };

  return assoc('resortMappingHistories', newObj, state);

}
